import { createContext, useEffect, useReducer } from 'react';

// utils
import axios from 'src/utils/axios';
import { isValidToken, setSession } from './Jwt';

// ----------------------------------------------------------------------
export interface InitialStateType {
  isAuthenticated: boolean;
  isInitialized?: boolean;
  user?: any | null | undefined;
  role?: any | null | undefined;
}

const initialState: InitialStateType = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  role: window.localStorage.getItem('role'),
};

const handlers: any = {
  INITIALIZE: (state: InitialStateType, action: any) => {
    const { isAuthenticated, user, role } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      role,
    };
  },
  LOGIN: (state: InitialStateType, action: any) => {
    const { user, role } = action.payload;

    return {
      ...state,
      role,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: InitialStateType) => ({
    ...state,
    role: null,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state: InitialStateType, action: any) => {
    const { user, role } = action.payload;

    return {
      ...state,
      role: role,
      isAuthenticated: false,
      user,
    };
  },
};

const reducer = (state: InitialStateType, action: any) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<any | null>({
  ...initialState,
  method: 'jwt',
  register: () => Promise.resolve(),
  signin: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  adminsignin: () => Promise.resolve(),
  UserSignin: () => Promise.resolve(),
});

function AuthProvider({ children }: { children: React.ReactElement }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const role = window.localStorage.getItem('role');
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          if (role === 'admin') {
            const response = await axios.get('/admin/view');
            const { data } = response.data;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                role: role,
                user: data,
              },
            });
          } else if (role === 'teacher') {
            const response = await axios.get('/teacher/view');
            const { data } = response.data;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                role: role,
                user: data,
              },
            });
          } else if (role === 'user') {
            const response = await axios.get('/user/view');
            const { data } = response.data;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                role: role,
                user: data,
              },
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              role: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const signin = async (email: string, password: string) => {
    const response = await axios.post('/teacher/login', {
      email,
      password,
    });
    const { token, data, status, message } = response.data;
    if (status) {
      setSession(token);
      localStorage.setItem('role', 'teacher');
      dispatch({
        type: 'LOGIN',
        payload: {
          user: data,
          role: 'teacher',
        },
      });
    } else {
      throw new Error(message);
    }
  };
  const UserSignin = async (email: string, password: string) => {
    const response = await axios.post('/user/login', {
      email,
      password,
    });

    const { token, data, status, message } = response.data;
    if (status) {
      setSession(token);
      localStorage.setItem('role', 'user');
      dispatch({
        type: 'LOGIN',
        payload: {
          user: data,
          role: 'user',
        },
      });
    } else {
      throw new Error(message);
    }
  };
  const adminsignin = async (email: string, password: string) => {
    const response = await axios.post('/admin/login', {
      email,
      password,
    });
    const { token, data, status, message } = response.data;
    if (status) {
      localStorage.setItem('role', 'admin');

      setSession(token);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: data,
          role: 'admin',
        },
      });
    } else {
      throw new Error(message);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    const response = await axios.post('/teacher/register', {
      email,
      password,
      name,
    });

    console.log(response);
    // const { accessToken} = response.data;

    // window.localStorage.setItem('accessToken', accessToken);
    // dispatch({
    //   type: 'REGISTER',
    //   payload: {
    //     user,
    //   },
    // });
  };

  const logout = async () => {
    setSession(null);
    localStorage.clear();
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        signin,
        logout,
        register,
        adminsignin,
        UserSignin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
